import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';
import { AuthGuard } from '@classroom-ai/ui/auth/guard';
import { usePathname } from '@classroom-ai/ui/routes/hooks';
import { LoadingScreen } from '@classroom-ai/ui/components/loading-screen';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { TeacherGuard } from 'src/auth/teacher-guard';
import { SchoolStaffGuard } from 'src/auth/school-staff-guard';

// ----------------------------------------------------------------------

const DashboardPage   = lazy(() => import('src/pages/dashboard'));
const DistrictPage    = lazy(() => import('src/pages/dashboard/district'));
const DistrictSchoolPage = lazy(() => import('src/pages/dashboard/district/school'));
const DepartmentPage  = lazy(() => import('src/pages/dashboard/department'));
const ClassOverviewPage = lazy(() => import('src/pages/dashboard/class-overview'));
const SessionsPage    = lazy(() => import('src/pages/dashboard/sessions'));
const CalendarPage    = lazy(() => import('src/pages/dashboard/calendar'));
const ClassPage       = lazy(() => import('src/pages/dashboard/class'));
const StudentPage     = lazy(() => import('src/pages/dashboard/student'));
const CurriculumPage  = lazy(() => import('src/pages/dashboard/curriculum'));
const StandardsPage   = lazy(() => import('src/pages/dashboard/standards'));
const ReviewsPage     = lazy(() => import('src/pages/dashboard/reviews'));
const LessonPlansPage = lazy(() => import('src/pages/dashboard/lesson-plans/list'));
const UnitLessonsPage = lazy(() => import('src/pages/dashboard/lesson-plans/unit'));
const LessonEditorPage = lazy(() => import('src/pages/dashboard/lesson-plans/editor'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <TeacherGuard>
    <DashboardLayout>
      <SuspenseOutlet />
    </DashboardLayout>
  </TeacherGuard>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { element: <DashboardPage />, index: true },
      {
        path: 'district',
        children: [
          { element: <DistrictPage />, index: true },
          { path: 'schools/:schoolId', element: <DistrictSchoolPage /> },
        ],
      },
      // Class-scoped routes — SchoolStaffGuard denies DISTRICT_ADMIN (no class context)
      // with a 403 view instead of an endless spinner. Nav already hides these for them.
      {
        element: <SchoolStaffGuard />,
        children: [
          { path: 'department', element: <DepartmentPage /> },
          { path: 'class-overview', element: <ClassOverviewPage /> },
          { path: 'sessions', element: <SessionsPage /> },
          { path: 'calendar', element: <CalendarPage /> },
          {
            path: 'class',
            children: [
              { element: <ClassPage />, index: true },
              // The student page (epic #595). The old `/sessions` URL keeps working: it renders
              // the same page on its Sessions tab, so `?thread=` / `?alert=` deep links survive.
              { path: 'students/:uid', element: <StudentPage /> },
              { path: 'students/:uid/sessions', element: <StudentPage /> },
            ],
          },
          { path: 'curriculum', element: <CurriculumPage /> },
          { path: 'standards', element: <StandardsPage /> },
          { path: 'reviews', element: <ReviewsPage /> },
          {
            path: 'lesson-plans',
            children: [
              { element: <LessonPlansPage />, index: true },
              { path: ':unitId', element: <UnitLessonsPage /> },
              { path: ':unitId/:lessonId', element: <LessonEditorPage /> },
            ],
          },
        ],
      },
    ],
  },
];
